import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchOrderDetail, revealPhone } from "../services/orders.service";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";
import PasswordModal from "../components/ui/PasswordModal";
import "../styles/orders.css";
import {
  LuArrowLeft,
  LuCalendar,
  LuUser,
  LuPackage,
  LuCreditCard,
  LuMapPin,
  LuPhone,
  LuMail,
  LuReceipt,
  LuEye,
  LuEyeOff,
  LuCheck,
  LuClock,
  LuX,
} from "react-icons/lu";

/**
 * Helper function to get location info based on order type
 * @param {Object} order - Order object
 * @returns {string} Formatted location string
 */
const getLocationInfo = (order) => {
  const orderType = order?.orderType;

  switch (orderType) {
    case "room-service":
      return `Room Number - ${order?.room_number ?? "_"}`;
    case "dine-in":
      return `Table Number - ${order?.table_number ?? "_"}`;
    case "takeaway":
      return "Table Number - 0";
    default:
      return "_";
  }
};

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const user = getUser() || DEV_USER;
  const ORG_ID = user.restaurant_id || 101;
  const LOCATION = user.location || "kphb";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Password verification state
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [revealedCustomer, setRevealedCustomer] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Memoized location info to prevent unnecessary re-renders
  const locationInfo = useMemo(() => {
    if (!order) return "_";
    return getLocationInfo(order);
  }, [order]);

  // Memoized status badge styling
  const locationBadgeStyle = useMemo(() => {
    if (!order?.orderType) {
      return {
        backgroundColor: "#e0e0e0",
        color: "#555",
      };
    }

    const colorMap = {
      "room-service": { bg: "#f28c28", text: "#fff" },
      "dine-in": { bg: "#f28c28", text: "#fff" },
      "takeaway": { bg: "#f28c28", text: "#fff" },
    };

    const colors = colorMap[order.orderType] || { bg: "#e0e0e0", text: "#555" };
    return {
      backgroundColor: colors.bg,
      color: colors.text,
    };
  }, [order?.orderType]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setErr(null);
      try {
        // Use the numeric ID directly for the API
        const json = await fetchOrderDetail(orderId, ORG_ID, LOCATION);
        if (!cancelled) {
          setOrder(json);
        }
      } catch (e) {
        if (!cancelled) setErr(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [orderId, ORG_ID, LOCATION]);

  const handleBack = () => {
    navigate("/orders");
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordError(null);
  };

  const handlePasswordSubmit = async (password) => {
    setIsVerifying(true);
    setPasswordError(null);
    try {
      // Extract order number from orderId (e.g., "ORD0782" -> "782")
      const orderNo = orderId.replace(/[^0-9]/g, "");

      const response = await revealPhone(ORG_ID, orderNo, LOCATION, password);

      // Store the revealed customer data
      setRevealedCustomer(response.customer);
      setPhoneVisible(true);
      setShowPasswordModal(false);
    } catch (error) {
      setPasswordError(
        error.response?.data?.message || "Invalid password. Please try again."
      );
    } finally {
      setIsVerifying(false);
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone;
  };

  // Mask phone number for hidden state
  const getMaskedPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length <= 4) return "••••";
    return "••••" + digits.slice(-4);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      paid: "status-paid",
      created: "status-created",
      completed: "status-completed",
      canceled: "status-canceled",
      on_process: "status-processing",
      pending: "status-pending",
    };
    return statusMap[status] || "status-default";
  };

  // Get payment status icon based on status
  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <LuCheck size={14} />;
      case "pending":
        return <LuClock size={14} />;
      case "canceled":
        return <LuX size={14} />;
      default:
        return <LuCheck size={14} />;
    }
  };

  // Get formatted payment status display text
  const getPaymentStatusText = (status) => {
    const textMap = {
      paid: "Paid",
      created: "Created",
      completed: "Completed",
      canceled: "Canceled",
      on_process: "Processing",
      pending: "Pending",
    };
    return textMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  if (loading) {
    return (
      <div className="page page--order-details">
        <div className="od-loading">
          <div className="od-loading-spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="page page--order-details">
        <div className="od-error">
          <div className="od-error-icon">⚠️</div>
          <h2>Couldn't load order details</h2>
          <p>{String(err.message || err)}</p>
          <button onClick={handleBack} className="od-btn od-btn--primary">
            <LuArrowLeft size={18} />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page page--order-details">
        <div className="od-error">
          <div className="od-error-icon">📦</div>
          <h2>Order not found</h2>
          <p>The order you're looking for doesn't exist.</p>
          <button onClick={handleBack} className="od-btn od-btn--primary">
            <LuArrowLeft size={18} />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const dateInfo = formatDate(order.placedAt || order.date);
  const orderIdDisplay = order.orderId || order.id || orderId;

  return (
    <div className="page page--order-details">
      {/* Header */}
      <div className="od-header">
        <button onClick={handleBack} className="od-back-btn">
          <LuArrowLeft size={20} />
          <span>Back to Orders</span>
        </button>
        <div className="od-header-title">
          <h1>Order Details</h1>
          <span className="od-summary-value">
            <span
              className={`od-payment-badge od-payment-badge--${getStatusClass(
                order.status
              )}`}
            >
              <span className="od-payment-badge__icon">
                {getPaymentStatusIcon(order.status)}
              </span>
              <span className="od-payment-badge__text">
                {getPaymentStatusText(order.status)}
              </span>
            </span>
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="od-content">
        {/* Left Column */}
        <div className="od-main">
          {/* Order Summary Card */}
          <div className="od-card">
            <div className="od-card-header">
              <h2
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: 0,
                }}
              >
                <LuReceipt size={20} />
                Order Summary
                <span
                  style={{
                    marginLeft: "auto",
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: 600,
                    borderRadius: "6px",
                    ...locationBadgeStyle,
                    whiteSpace: "nowrap",
                  }}
                >
                  {locationInfo}
                </span>
              </h2>
            </div>
            <div className="od-card-body">
              <div className="od-summary-grid">
                <div className="od-summary-item">
                  <span className="od-summary-label">Order ID</span>
                  <span className="od-summary-value">{orderIdDisplay}</span>
                </div>
                <div className="od-summary-item">
                  <span className="od-summary-label">
                    <LuCalendar size={16} />
                    Date & Time
                  </span>
                  <span className="od-summary-value">
                    <div>{dateInfo.date}</div>
                    <div className="od-time">{dateInfo.time}</div>
                  </span>
                </div>
                <div className="od-summary-item">
                  <span className="od-summary-label">
                    <LuPackage size={16} />
                    Order Type
                  </span>
                  <span className="od-summary-value">
                    <span
                      className={`od-type-badge od-type-${
                        order.orderType || order.order_type || "default"
                      }`}
                    >
                      {order.orderType || order.order_type || "N/A"}
                    </span>
                  </span>
                </div>
                <div className="od-summary-item">
                  <span className="od-summary-label">
                    <LuCreditCard size={16} />
                    Payment Status
                  </span>
                  <span className="od-summary-value">
                    <span
                      className={`od-payment-badge od-payment-badge--${getStatusClass(
                        order.status
                      )}`}
                    >
                      <span className="od-payment-badge__icon">
                        {getPaymentStatusIcon(order.status)}
                      </span>
                      <span className="od-payment-badge__text">
                        {getPaymentStatusText(order.status)}
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Items Card */}
          {order.items && order.items.length > 0 && (
            <div className="od-card">
              <div className="od-card-header">
                <h2>
                  <LuPackage size={20} />
                  Order Items ({order.items.length})
                </h2>
              </div>
              <div className="od-card-body">
                <div className="od-items-list">
                  {order.items.map((item, index) => (
                    <div key={index} className="od-item-row">
                      <div className="od-item-info">
                        <div className="od-item-number">{index + 1}</div>
                        <div className="od-item-details">
                          <h3 className="od-item-name">
                            {item.name || item.item_name || "Item"}
                          </h3>
                          {item.description && (
                            <p className="od-item-desc">{item.description}</p>
                          )}
                          <div className="od-item-meta">
                            {item.quantity && (
                              <span className="od-item-qty">
                                Qty: {item.quantity}
                              </span>
                            )}
                            {item.qty && (
                              <span className="od-item-qty">
                                Qty: {item.qty}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="od-item-price">
                        {item.unitPrice * item.qty ? (
                          <span>
                            ₹
                            {(item.unitPrice * item.qty).toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 2 }
                            )}
                          </span>
                        ) : (
                          <span>—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Cooking Instructions Card */}
          <div className="od-card">
            <div className="od-card-header">
              <h2>Cooking Instructions</h2>
            </div>
            <div className="od-card-body">
              <div className="cooking-instructions">
                <span className="value">{order.notes || "None"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="od-sidebar">
          {/* Customer Info Card */}
          <div
            className="od-card is-clickable"
            // onClick={() => setShowPasswordModal(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowPasswordModal(true);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Customer Information - Click to view details"
            style={{ cursor: "pointer" }}
          >
            <div className="od-card-header">
              <h2>
                <LuUser size={20} />
                Customer Information
              </h2>
            </div>
            <div className="od-card-body">
              <div className="od-customer-info">
                {/* Customer name - use revealed customer name or fallback */}
                {revealedCustomer?.name || order.customer?.name ? (
                  <div className="od-customer-name">
                    {revealedCustomer?.name || order.customer?.name}
                  </div>
                ) : revealedCustomer?.id ? (
                  <div className="od-customer-name">
                    Customer {revealedCustomer.id}
                  </div>
                ) : (
                  <div className="od-customer-name">Guest Customer</div>
                )}

                {/* Phone number section - from revealed customer API response */}
                {revealedCustomer?.id ? (
                  <div className="od-customer-id">
                    {/* Phone Number: {revealedCustomer.id} */}
                  </div>
                ) : order.customer?.id ? (
                  <div className="od-customer-id">
                    {/* Phone Number: {order.customer.id} */}
                  </div>
                ) : null}

                {/* Phone display with show/hide functionality */}
                {(revealedCustomer?.id ||
                  order.customer?.phone ||
                  order.customer?.id) && (
                  <div className="od-customer-contact od-phone-container">
                    <div className="od-phone-icon">
                      <LuPhone size={16} />
                    </div>
                    {phoneVisible ? (
                      <div className="od-phone-visible">
                        <span className="od-phone-number">
                          {formatPhoneNumber(
                            revealedCustomer?.id ||
                              order.customer?.phone ||
                              order.customer?.id
                          )}
                        </span>
                        <button
                          className="od-hide-phone-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPhoneVisible(false);
                          }}
                          title="Hide phone number"
                        >
                          <LuEyeOff size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className="od-phone-hidden"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPasswordModal(true);
                        }}
                        title="Click to reveal phone number"
                      >
                        <span className="od-phone-masked">
                          {getMaskedPhone(
                            revealedCustomer?.id ||
                              order.customer?.phone ||
                              order.customer?.id
                          )}
                        </span>
                        <span className="od-phone-reveal">
                          <LuEye size={14} />
                          <span>Show</span>
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {/* Email */}
                {(revealedCustomer?.email || order.customer?.email) && (
                  <div className="od-customer-contact">
                    <LuMail size={16} />
                    {revealedCustomer?.email || order.customer?.email}
                  </div>
                )}

                {/* Address */}
                {(revealedCustomer?.address || order.customer?.address) && (
                  <div className="od-customer-contact">
                    <LuMapPin size={16} />
                    {revealedCustomer?.address || order.customer?.address}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Summary Card */}
          <div className="od-card">
            <div className="od-card-header">
              <h2>Payment Summary</h2>
            </div>
            <div className="od-card-body">
              <div className="od-payment-summary">
                <div className="od-payment-row">
                  <span className="od-payment-label">Subtotal</span>
                  <span className="od-payment-value">
                    ₹
                    {order.subtotal.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="od-payment-row">
                  <span className="od-payment-label">Tax</span>
                  <span className="od-payment-value">
                    ₹
                    {(order.tax ?? 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="od-payment-row">
                  <span className="od-payment-label">Packing Charge</span>
                  <span className="od-payment-value">
                    ₹
                    {order.packingCharge.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="od-payment-row">
                  <span className="od-payment-label">Discount</span>
                  <span className="od-payment-value">
                    ₹
                    {(order.discount ?? 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="od-payment-row od-payment-total">
                  <span className="od-payment-label">Total Amount</span>
                  <span className="od-payment-value">
                    ₹
                    {(order.amount || order.total || 0).toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal for phone verification */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={handleClosePasswordModal}
        onSubmit={handlePasswordSubmit}
        loading={isVerifying}
        error={passwordError}
        title="Verify Password"
        description="Enter the 4-digit password to reveal the customer phone number."
      />
    </div>
  );
}
